import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CANVAS_PRESETS, AspectRatioSpec, getPlatformLabel, getPostTypesLabel } from '../config/metaFormats';
import { getPhosphorIcon } from '../config/phosphorIcons';

// Types
export interface ElementPosition {
  x: number; // Percentage
  y: number;
}

export interface ElementSize {
  width: number; // Percentage
  height: number;
}

export interface ElementStyle {
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  lineHeight?: number;
  letterSpacing?: number;
  textDecoration?: 'none' | 'underline' | 'line-through';
  textShadow?: string;
  backgroundColor?: string;
  backgroundGradient?: string;
  borderRadius?: number;
  border?: string;
  borderWidth?: number;
  borderColor?: string;
  borderStyle?: 'solid' | 'dashed' | 'dotted';
  padding?: number; // Innenabstand in px
  shapeType?: 'rectangle' | 'circle' | 'ellipse' | 'line' | 'triangle' | 'star' | 'custom';
  svgContent?: string; // SVG-Inhalt für custom shapes aus Element-Bibliothek
  libraryElementId?: string; // ID des Elements aus der Bibliothek
  phosphorIcon?: string; // Name des Phosphor Icons
  phosphorWeight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'; // Phosphor Icon Gewicht
  fill?: boolean;
  strokeOnly?: boolean;
  objectFit?: 'cover' | 'contain' | 'fill';
  imageOffsetX?: number; // Bild-Position innerhalb der Form (Prozent, -100 bis 100)
  imageOffsetY?: number; // Bild-Position innerhalb der Form (Prozent, -100 bis 100)
  imageScale?: number; // Bild-Zoom innerhalb der Form (1 = 100%)
  opacity?: number;
  rotation?: number; // Z-Achse Rotation (2D)
  rotateX?: number; // X-Achse 3D-Rotation (kippt nach vorne/hinten)
  rotateY?: number; // Y-Achse 3D-Rotation (kippt nach links/rechts)
  skewX?: number; // Horizontales Scheren in Grad
  skewY?: number; // Vertikales Scheren in Grad
  perspective?: number; // Perspektiv-Tiefe für 3D-Effekt (px)
  shadow?: string;
  elevation?: number; // 3D-Effekt: 0-24
  strokeWidth?: number; // Liniendicke
  zIndex?: number;
  // Audio-spezifische Styles
  volume?: number; // 0-100
  fadeInDuration?: number; // Fade-In in Sekunden
  fadeOutDuration?: number; // Fade-Out in Sekunden
  audioLoop?: boolean; // Wiederholen
  audioStartTime?: number; // Startzeit im Audio in Sekunden
  audioEndTime?: number; // Endzeit im Audio in Sekunden
}

export interface CanvasElement {
  id: string;
  type: 'text' | 'image' | 'shape' | 'group' | 'audio';
  position: ElementPosition;
  size: ElementSize;
  style: ElementStyle;
  content?: string;
  locked?: boolean;
  lockAspectRatio?: boolean; // Seitenverhältnis beim Resizen beibehalten
  groupId?: string; // ID der Gruppe, zu der dieses Element gehört
  children?: string[]; // Für Gruppen: IDs der Kind-Elemente
}

export interface CanvasSettings {
  width: number;
  height: number;
  background_type: 'color' | 'gradient' | 'image';
  background_value: string;
}

interface CanvasEditorProps {
  canvas: CanvasSettings;
  elements: CanvasElement[];
  selectedElementId: string | null;
  selectedElementIds?: string[]; // Multi-Selektion
  onCanvasChange: (canvas: CanvasSettings) => void;
  onElementsChange: (elements: CanvasElement[]) => void;
  onSelectElement: (id: string | null, addToSelection?: boolean) => void;
  onSelectMultiple?: (ids: string[]) => void;
  onAddElement: (type: 'text' | 'image' | 'shape' | 'audio') => void;
  onOpenElementLibrary?: () => void;
  onGroupElements?: (elementIds: string[]) => void;
  onUngroupElements?: (groupId: string) => void;
  previewMode?: boolean; // Vorschau-Modus: versteckt Auswahl-Rahmen und Element-Borders
  onPreviewModeChange?: (enabled: boolean) => void;
}

// Alignment types
type AlignmentType = 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom';
type DistributeType = 'horizontal' | 'vertical';

// Toolbar für Element-Typen
function ElementToolbar({
  onAddElement,
  onOpenElementLibrary,
  selectedCount = 0,
  onGroup,
  onUngroup,
  canUngroup = false,
  onAlign,
  onDistribute,
  hasSelection = false,
}: {
  onAddElement: (type: 'text' | 'image' | 'shape' | 'audio') => void;
  onOpenElementLibrary?: () => void;
  selectedCount?: number;
  onGroup?: () => void;
  onUngroup?: () => void;
  canUngroup?: boolean;
  onAlign?: (type: AlignmentType) => void;
  onDistribute?: (type: DistributeType) => void;
  hasSelection?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-100 rounded-lg mb-4">
      <span className="text-sm text-gray-500 mr-2">{t('creator:add')}:</span>
      <button
        onClick={() => onAddElement('text')}
        className="p-2 hover:bg-white rounded-lg transition-colors"
        title={t('creator:addText')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      </button>
      <button
        onClick={() => onAddElement('image')}
        className="p-2 hover:bg-white rounded-lg transition-colors"
        title={t('creator:addImage')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      <button
        onClick={() => onAddElement('shape')}
        className="p-2 hover:bg-white rounded-lg transition-colors"
        title={t('creator:addShape')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
        </svg>
      </button>
      {onOpenElementLibrary && (
        <button
          onClick={onOpenElementLibrary}
          className="p-2 hover:bg-white rounded-lg transition-colors bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200"
          title={t('creator:library.openLibrary')}
        >
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
        </button>
      )}
      <button
        onClick={() => onAddElement('audio')}
        className="p-2 hover:bg-white rounded-lg transition-colors"
        title={t('creator:addAudio')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      </button>

      {/* Trennlinie vor Ausrichtung */}
      {hasSelection && onAlign && (
        <>
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <span className="text-sm text-gray-500">{t('creator:alignLabel')}:</span>

          {/* Horizontale Ausrichtung */}
          <button
            onClick={() => onAlign('left')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:alignLeft')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4v16M8 8h12M8 16h8" />
            </svg>
          </button>
          <button
            onClick={() => onAlign('center-h')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:centerHorizontally')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M12 4v16M6 8h12M8 16h8" />
            </svg>
          </button>
          <button
            onClick={() => onAlign('right')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:alignRight')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M20 4v16M4 8h12M8 16h8" />
            </svg>
          </button>

          <div className="w-px h-4 bg-gray-300" />

          {/* Vertikale Ausrichtung */}
          <button
            onClick={() => onAlign('top')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:alignTop')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4h16M8 8v12M16 8v8" />
            </svg>
          </button>
          <button
            onClick={() => onAlign('center-v')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:centerVertically')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M4 12h16M8 6v12M16 8v8" />
            </svg>
          </button>
          <button
            onClick={() => onAlign('bottom')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:alignBottom')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M4 20h16M8 4v12M16 8v8" />
            </svg>
          </button>
        </>
      )}

      {/* Verteilen (nur bei mehreren Elementen) */}
      {selectedCount > 2 && onDistribute && (
        <>
          <div className="w-px h-6 bg-gray-300 mx-1" />
          <span className="text-sm text-gray-500">{t('creator:distribute')}:</span>
          <button
            onClick={() => onDistribute('horizontal')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:distributeHorizontally')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4v16M12 6v12M20 4v16" />
              <path d="M7 12h2M15 12h2" strokeLinecap="round" />
            </svg>
          </button>
          <button
            onClick={() => onDistribute('vertical')}
            className="p-1.5 hover:bg-white rounded transition-colors"
            title={t('creator:distributeVertically')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4h16M6 12h12M4 20h16" />
              <path d="M12 7v2M12 15v2" strokeLinecap="round" />
            </svg>
          </button>
        </>
      )}

      {/* Trennlinie vor Gruppierung */}
      {(selectedCount > 1 || canUngroup) && (
        <div className="w-px h-6 bg-gray-300 mx-1" />
      )}

      {/* Gruppieren-Button (wenn mehrere ausgewählt) */}
      {selectedCount > 1 && onGroup && (
        <button
          onClick={onGroup}
          className="p-2 hover:bg-white rounded-lg transition-colors text-green-600"
          title={t('creator:group')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
          </svg>
        </button>
      )}

      {/* Gruppierung aufheben */}
      {canUngroup && onUngroup && (
        <button
          onClick={onUngroup}
          className="p-2 hover:bg-white rounded-lg transition-colors text-orange-600"
          title={t('creator:ungroup')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 19h6M14 15h6" />
          </svg>
        </button>
      )}

      {/* Info über Auswahl */}
      {selectedCount > 1 && (
        <span className="text-xs text-gray-400 ml-2">{selectedCount} {t('creator:selectedCount')}</span>
      )}
    </div>
  );
}

// Format-Auswahl
function FormatSelector({
  currentCanvas,
  onSelect,
}: {
  currentCanvas: CanvasSettings;
  onSelect: (preset: AspectRatioSpec) => void;
}) {
  const { t } = useTranslation('creator');
  const currentPreset = CANVAS_PRESETS.find(
    p => p.width === currentCanvas.width && p.height === currentCanvas.height
  );

  // Get translated name for preset
  const getPresetName = (preset: AspectRatioSpec): string => {
    if (preset.nameKey) {
      return t(`canvasPresets.${preset.nameKey}`, { defaultValue: preset.name });
    }
    return preset.name;
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">{t('format')}</label>
      <select
        value={`${currentCanvas.width}x${currentCanvas.height}`}
        onChange={(e) => {
          const [width, height] = e.target.value.split('x').map(Number);
          const preset = CANVAS_PRESETS.find(p => p.width === width && p.height === height);
          if (preset) onSelect(preset);
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      >
        {CANVAS_PRESETS.map((preset) => {
          const platformLabel = getPlatformLabel(preset);
          return (
            <option key={`${preset.width}x${preset.height}`} value={`${preset.width}x${preset.height}`}>
              {getPresetName(preset)} ({preset.ratio}) [{platformLabel}]
            </option>
          );
        })}
      </select>
      {/* Plattform-Info unter dem Dropdown */}
      {currentPreset && currentPreset.platforms && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className={`px-2 py-0.5 rounded font-medium ${
            currentPreset.platforms.length === 2
              ? 'bg-purple-100 text-purple-700'
              : currentPreset.platforms[0] === 'facebook'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-pink-100 text-pink-700'
          }`}>
            {currentPreset.platforms.length === 2 ? '📱 FB + IG' :
             currentPreset.platforms[0] === 'facebook' ? '📘 Facebook' : '📸 Instagram'}
          </span>
          <span className="text-gray-500">
            {getPostTypesLabel(currentPreset)}
          </span>
        </div>
      )}
    </div>
  );
}

// Einzelnes Element im Canvas
function CanvasElementComponent({
  element,
  isSelected,
  isMultiSelected = false,
  scale,
  previewMode = false,
  onSelect,
  onMove,
  onResize,
  onRotate,
}: {
  element: CanvasElement;
  isSelected: boolean;
  isMultiSelected?: boolean;
  scale: number;
  previewMode?: boolean;
  onSelect: (addToSelection?: boolean) => void;
  onMove: (dx: number, dy: number) => void;
  onResize: (dw: number, dh: number, handle: string) => void;
  onRotate: (angle: number) => void;
}) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const rotationStartRef = useRef({ angle: 0, centerX: 0, centerY: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Cmd+Click (Mac) oder Shift+Click für Multi-Selection
    const addToSelection = e.metaKey || e.shiftKey;
    onSelect(addToSelection);
    if (element.locked) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  // Handle click to select without drag
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Cmd+Click (Mac) oder Shift+Click für Multi-Selection
    const addToSelection = e.metaKey || e.shiftKey;
    onSelect(addToSelection);
  };

  const handleResizeStart = (e: React.MouseEvent, handle: string) => {
    if (element.locked) return;
    e.stopPropagation();
    setIsResizing(handle);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleRotateStart = (e: React.MouseEvent) => {
    if (element.locked) return;
    e.stopPropagation();
    e.preventDefault();
    setIsRotating(true);

    // Berechne Mittelpunkt des Elements
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Berechne initialen Winkel zum Mauszeiger
      const startAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);

      rotationStartRef.current = {
        angle: element.style.rotation || 0,
        centerX,
        centerY,
      };
      dragStartRef.current = { x: startAngle, y: 0 };
    }
  };

  useEffect(() => {
    if (!isDragging && !isResizing && !isRotating) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isRotating) {
        // Rotationslogik
        const { centerX, centerY, angle: startRotation } = rotationStartRef.current;
        const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
        const startAngle = dragStartRef.current.x;
        let deltaAngle = currentAngle - startAngle;

        // Neuer Rotationswinkel
        let newRotation = startRotation + deltaAngle;

        // Snap zu 15-Grad-Schritten wenn Shift gedrückt
        if (e.shiftKey) {
          newRotation = Math.round(newRotation / 15) * 15;
        }

        // Normalisiere auf 0-360
        while (newRotation < 0) newRotation += 360;
        while (newRotation >= 360) newRotation -= 360;

        onRotate(newRotation);
      } else {
        const dx = (e.clientX - dragStartRef.current.x) / scale;
        const dy = (e.clientY - dragStartRef.current.y) / scale;

        if (isDragging) {
          onMove(dx, dy);
        } else if (isResizing) {
          onResize(dx, dy, isResizing);
        }

        dragStartRef.current = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(null);
      setIsRotating(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, isRotating, scale, onMove, onResize, onRotate]);

  // Build transform string with 3D rotation, 2D rotation and skew
  const buildTransform = () => {
    const transforms: string[] = [];

    // 3D-Transformationen (X/Y-Achse Rotation)
    if (element.style.rotateX) {
      transforms.push(`rotateX(${element.style.rotateX}deg)`);
    }
    if (element.style.rotateY) {
      transforms.push(`rotateY(${element.style.rotateY}deg)`);
    }

    // 2D-Rotation (Z-Achse)
    if (element.style.rotation) {
      transforms.push(`rotateZ(${element.style.rotation}deg)`);
    }

    // Scheren (Skew)
    if (element.style.skewX) {
      transforms.push(`skewX(${element.style.skewX}deg)`);
    }
    if (element.style.skewY) {
      transforms.push(`skewY(${element.style.skewY}deg)`);
    }

    return transforms.length > 0 ? transforms.join(' ') : undefined;
  };

  // Check if 3D transforms are used
  const has3DTransform = element.style.rotateX || element.style.rotateY;
  const perspectiveValue = element.style.perspective || (has3DTransform ? 1000 : 0);

  // Build elevation shadow
  const buildElevationShadow = () => {
    const elevation = element.style.elevation ?? 0;
    if (elevation === 0) return element.style.shadow;
    // Material Design-style elevation shadows
    const shadowY = elevation * 0.5;
    const shadowBlur = elevation * 1.5;
    const shadowSpread = elevation * 0.2;
    const opacity = Math.min(0.3, 0.1 + elevation * 0.01);
    return `0 ${shadowY}px ${shadowBlur}px ${shadowSpread}px rgba(0, 0, 0, ${opacity})`;
  };

  // Berechne Pixel-Position und -Größe
  const style: React.CSSProperties = {
    position: 'absolute',
    left: `${element.position.x}%`,
    top: `${element.position.y}%`,
    width: `${element.size.width}%`,
    height: `${element.size.height}%`,
    cursor: element.locked ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
    opacity: element.style.opacity ?? 1,
    transform: buildTransform(),
    perspective: perspectiveValue > 0 ? `${perspectiveValue}px` : undefined,
    transformStyle: has3DTransform ? 'preserve-3d' : undefined,
    boxShadow: buildElevationShadow(),
    zIndex: element.style.zIndex ?? 0,
  };

  // Element-spezifische Styles
  if (element.type === 'text') {
    const textBorderWidth = element.style.borderWidth || 0;
    // Font size muss mit dem Canvas skaliert werden
    // Die fontSize wird relativ zur Canvas-Höhe berechnet und mit scale multipliziert
    const baseFontSize = element.style.fontSize || 24;
    const scaledFontSize = baseFontSize * scale;
    const scaledLetterSpacing = element.style.letterSpacing ? element.style.letterSpacing * scale : undefined;
    const scaledBorderWidth = textBorderWidth * scale;
    const scaledBorderRadius = element.style.borderRadius ? element.style.borderRadius * scale : undefined;
    const scaledPadding = element.style.padding ? element.style.padding * scale : 4 * scale;

    // Build font-family with proper fallbacks
    // Avenir Next is the preferred font (macOS/iOS native), with Nunito as cross-platform fallback
    const fontFamily = element.style.fontFamily || 'Avenir Next';
    const fontFamilyWithFallback = `"${fontFamily}", "Nunito", system-ui, -apple-system, sans-serif`;

    Object.assign(style, {
      fontFamily: fontFamilyWithFallback,
      fontSize: `${scaledFontSize}px`,
      fontWeight: element.style.fontWeight || 'normal',
      fontStyle: element.style.fontStyle || 'normal',
      color: element.style.color || '#000000',
      textAlign: element.style.textAlign || 'left',
      lineHeight: element.style.lineHeight || 1.2,
      letterSpacing: scaledLetterSpacing ? `${scaledLetterSpacing}px` : undefined,
      textDecoration: element.style.textDecoration || 'none',
      textShadow: element.style.textShadow,
      backgroundColor: element.style.backgroundColor,
      borderRadius: scaledBorderRadius ? `${scaledBorderRadius}px` : undefined,
      border: scaledBorderWidth > 0
        ? `${scaledBorderWidth}px ${element.style.borderStyle || 'solid'} ${element.style.borderColor || '#000000'}`
        : undefined,
      display: 'flex',
      alignItems: element.style.verticalAlign === 'middle' ? 'center' :
                  element.style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
      justifyContent: element.style.textAlign === 'center' ? 'center' :
                      element.style.textAlign === 'right' ? 'flex-end' : 'flex-start',
      padding: `${scaledPadding}px`,
      overflow: 'hidden',
    });
  } else if (element.type === 'shape') {
    const shapeType = element.style.shapeType || 'rectangle';
    // Use strokeWidth or borderWidth for border - skaliert mit Canvas
    const strokeW = (element.style.strokeWidth || element.style.borderWidth || 0) * scale;
    const scaledShapeBorderRadius = element.style.borderRadius ? element.style.borderRadius * scale : undefined;

    // Support for both solid color and gradient fills
    const hasGradient = !!element.style.backgroundGradient;
    const shapeColor = element.style.backgroundColor || '#3B82F6';
    const shapeFill = hasGradient ? element.style.backgroundGradient : shapeColor;

    // Basis-Styles für alle Formen
    Object.assign(style, {
      backgroundColor: 'transparent',
      overflow: 'visible',
    });

    // Nur rectangle, circle und ellipse bekommen direkte CSS-Styles
    // Andere Formen (triangle, star, line) werden per SVG gerendert
    switch (shapeType) {
      case 'circle':
        // Kreis: Immer 1:1 Seitenverhältnis, nutzt die kleinere Dimension
        Object.assign(style, {
          // Use 'background' for gradients, 'backgroundColor' for solid colors
          ...(element.style.strokeOnly ? {} : hasGradient
            ? { background: shapeFill }
            : { backgroundColor: shapeColor }),
          borderRadius: '50%',
          border: strokeW > 0
            ? `${strokeW}px ${element.style.borderStyle || 'solid'} ${element.style.borderColor || '#000'}`
            : undefined,
          overflow: 'hidden', // Für Bild-Füllung
        });
        break;

      case 'ellipse':
        Object.assign(style, {
          ...(element.style.strokeOnly ? {} : hasGradient
            ? { background: shapeFill }
            : { backgroundColor: shapeColor }),
          borderRadius: '50%',
          border: strokeW > 0
            ? `${strokeW}px ${element.style.borderStyle || 'solid'} ${element.style.borderColor || '#000'}`
            : undefined,
          overflow: 'hidden', // Für Bild-Füllung
        });
        break;

      case 'rectangle':
      default:
        if (shapeType === 'rectangle' || !['triangle', 'star', 'line'].includes(shapeType)) {
          Object.assign(style, {
            ...(element.style.strokeOnly ? {} : hasGradient
              ? { background: shapeFill }
              : { backgroundColor: shapeColor }),
            borderRadius: scaledShapeBorderRadius ? `${scaledShapeBorderRadius}px` : undefined,
            border: element.style.border || (strokeW > 0
              ? `${strokeW}px ${element.style.borderStyle || 'solid'} ${element.style.borderColor || '#000'}`
              : undefined),
            overflow: 'hidden', // Für Bild-Füllung
          });
        }
        break;

      // triangle, star, line werden in der Render-Funktion als SVG dargestellt
      case 'triangle':
      case 'star':
      case 'line':
        // SVG-Rendering im Element-Content-Bereich
        break;
    }
  } else if (element.type === 'image') {
    const scaledImageBorderRadius = element.style.borderRadius ? element.style.borderRadius * scale : undefined;
    Object.assign(style, {
      borderRadius: scaledImageBorderRadius ? `${scaledImageBorderRadius}px` : undefined,
      overflow: 'hidden',
    });
  }

  // Selection-Styling: NUR bei aktiver Auswahl sichtbar, KEIN Hover-Rahmen
  // Im Preview-Modus: KEINE Auswahl-Rahmen, KEINE Outlines, KEINE Interaktion
  const selectionClass = (previewMode || !isSelected)
    ? '' // Preview-Modus oder nicht ausgewählt: keine Auswahl-Styling
    : isMultiSelected
      ? 'outline outline-1 outline-green-500/60 outline-offset-1'
      : 'outline outline-1 outline-blue-500/60 outline-offset-1';

  // Preview-Modus: Cursor ändern (kein grab), outline komplett deaktivieren
  // Explizit outline:none um alle Browser-Defaults und Tailwind-Classes zu überschreiben
  const finalStyle: React.CSSProperties = {
    ...style,
    outline: previewMode ? 'none' : (isSelected ? undefined : 'none'),
    outlineOffset: previewMode ? '0' : undefined,
    cursor: previewMode ? 'default' : style.cursor,
    // Im Preview-Modus: keine Pointer-Events (verhindert Hover-Effekte)
    pointerEvents: previewMode ? 'none' : 'auto',
    // WICHTIG: overflow:visible damit Rotation-Handle sichtbar ist
    // Der innere Content bekommt sein eigenes overflow:hidden
    overflow: 'visible',
  };

  return (
    <div
      ref={elementRef}
      style={finalStyle}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      className={`select-none ${selectionClass}`}
    >
      {/* Element Content - Text in eigenem Container für overflow:hidden */}
      {element.type === 'text' && (
        <div className="w-full h-full overflow-hidden flex" style={{
          alignItems: element.style.verticalAlign === 'middle' ? 'center' :
                      element.style.verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
          justifyContent: element.style.textAlign === 'center' ? 'center' :
                          element.style.textAlign === 'right' ? 'flex-end' : 'flex-start',
        }}>
          <span style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{element.content || 'Text'}</span>
        </div>
      )}
      {element.type === 'image' && element.content && (
        <div
          className="w-full h-full"
          style={{
            borderRadius: element.style.borderRadius ? `${element.style.borderRadius * scale}px` : undefined,
            overflow: 'hidden',
          }}
        >
          <img
            src={element.content}
            alt=""
            className="w-full h-full"
            style={{ objectFit: element.style.objectFit || 'cover' }}
            draggable={false}
          />
        </div>
      )}
      {element.type === 'image' && !element.content && (
        <div
          className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-400"
          style={{
            borderRadius: element.style.borderRadius ? `${element.style.borderRadius * scale}px` : undefined,
            overflow: 'hidden',
          }}
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      {/* Audio Element */}
      {element.type === 'audio' && (
        <div className="w-full h-full bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex flex-col items-center justify-center text-white p-2">
          <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
          </svg>
          {element.content ? (
            <span className="text-xs text-center truncate w-full">
              {element.content.split('/').pop()?.substring(0, 20) || 'Audio'}
            </span>
          ) : (
            <span className="text-xs text-purple-200">Kein Audio</span>
          )}
          {/* Fade-Indikator */}
          {(element.style.fadeInDuration || element.style.fadeOutDuration) && (
            <div className="flex items-center gap-2 mt-1 text-xs text-purple-200">
              {element.style.fadeInDuration && (
                <span>↗ {element.style.fadeInDuration}s</span>
              )}
              {element.style.fadeOutDuration && (
                <span>↘ {element.style.fadeOutDuration}s</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Shape Elements - SVG für proportionale Darstellung */}
      {element.type === 'shape' && (() => {
        const shapeType = element.style.shapeType || 'rectangle';
        const shapeColor = element.style.backgroundColor || '#3B82F6';
        const strokeColor = element.style.borderColor || '#000000';
        const strokeWidth = element.style.strokeWidth || element.style.borderWidth || 0;
        const strokeOnly = element.style.strokeOnly;

        // Gradient support for SVG shapes
        const hasGradientFill = !!element.style.backgroundGradient;
        const gradientId = `gradient-${element.id}`;

        // Parse gradient string to create SVG gradient definition
        const parseGradientForSVG = (gradientStr: string): { type: 'linear' | 'radial'; stops: Array<{ color: string; offset: number }>; angle: number } => {
          const isRadial = gradientStr.startsWith('radial-gradient');
          const stops: Array<{ color: string; offset: number }> = [];
          let angle = 180;

          // Extract angle for linear gradients
          const angleMatch = gradientStr.match(/linear-gradient\((\d+)deg/);
          if (angleMatch) {
            angle = parseInt(angleMatch[1]);
          }

          // Extract color stops
          const stopRegex = /(#[0-9A-Fa-f]{3,6}|rgb\([^)]+\)|rgba\([^)]+\))\s+(\d+(?:\.\d+)?)%/g;
          let match;
          while ((match = stopRegex.exec(gradientStr)) !== null) {
            stops.push({ color: match[1], offset: parseFloat(match[2]) / 100 });
          }

          return { type: isRadial ? 'radial' : 'linear', stops, angle };
        };

        const gradientData = hasGradientFill ? parseGradientForSVG(element.style.backgroundGradient!) : null;

        // Convert angle to SVG coordinates (CSS 0deg = top, SVG starts from left)
        const angleToCoords = (angle: number): { x1: string; y1: string; x2: string; y2: string } => {
          // CSS angles: 0deg = to top, 90deg = to right, 180deg = to bottom, 270deg = to left
          const rad = (angle - 90) * (Math.PI / 180);
          const x1 = 50 - Math.cos(rad) * 50;
          const y1 = 50 - Math.sin(rad) * 50;
          const x2 = 50 + Math.cos(rad) * 50;
          const y2 = 50 + Math.sin(rad) * 50;
          return { x1: `${x1}%`, y1: `${y1}%`, x2: `${x2}%`, y2: `${y2}%` };
        };

        const gradientCoords = gradientData ? angleToCoords(gradientData.angle) : null;

        // Für rectangle, circle und ellipse: Bild-Füllung anzeigen wenn vorhanden
        if ((shapeType === 'rectangle' || shapeType === 'circle' || shapeType === 'ellipse') && element.content) {
          // Bild-Offset und Zoom für CSS-basierte Formen
          const cssImgScale = element.style.imageScale ?? 1;
          const cssImgOffsetX = element.style.imageOffsetX ?? 0;
          const cssImgOffsetY = element.style.imageOffsetY ?? 0;
          // Bei Zoom: Bild vergrößern und per transform verschieben
          const imgStyle: React.CSSProperties = {
            objectFit: element.style.objectFit || 'cover',
            transform: `scale(${cssImgScale}) translate(${cssImgOffsetX / cssImgScale}%, ${cssImgOffsetY / cssImgScale}%)`,
            transformOrigin: 'center center',
          };

          // Container-Style für die Form (borderRadius für Clipping)
          const scaledBorderRadius = element.style.borderRadius ? element.style.borderRadius * scale : undefined;
          const containerStyle: React.CSSProperties = {
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            borderRadius: shapeType === 'circle' || shapeType === 'ellipse'
              ? '50%'
              : (scaledBorderRadius ? `${scaledBorderRadius}px` : undefined),
          };

          return (
            <div style={containerStyle}>
              <img
                src={element.content}
                alt=""
                className="w-full h-full"
                style={imgStyle}
                draggable={false}
              />
            </div>
          );
        }

        // Elevation/Shadow für SVG berechnen
        const elevation = element.style.elevation ?? 0;
        const hasElevation = elevation > 0;
        const shadowY = elevation * 0.5;
        const shadowBlur = elevation * 1.5;
        const shadowOpacity = Math.min(0.3, 0.1 + elevation * 0.01);

        // Bild-Offset und Zoom innerhalb der Form
        const imgOffsetX = element.style.imageOffsetX ?? 0;
        const imgOffsetY = element.style.imageOffsetY ?? 0;
        const imgScale = element.style.imageScale ?? 1;
        // Bild-Größe basierend auf Zoom (größer = mehr Spielraum zum Verschieben)
        const imgSize = 100 * imgScale;
        // Position so berechnen, dass das Bild zentriert ist und dann der Offset angewendet wird
        const imgX = (100 - imgSize) / 2 + imgOffsetX;
        const imgY = (100 - imgSize) / 2 + imgOffsetY;

        // SVG-basiertes Rendering für Dreieck, Stern, Linie
        if (shapeType === 'triangle') {
          // Determine fill: gradient URL, solid color, or none
          const triangleFill = strokeOnly ? 'none' : (hasGradientFill ? `url(#${gradientId})` : shapeColor);

          return (
            <svg className="w-full h-full" viewBox="-10 -10 120 120" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
              <defs>
                {/* Gradient definition for SVG */}
                {hasGradientFill && gradientData && gradientCoords && (
                  gradientData.type === 'linear' ? (
                    <linearGradient id={gradientId} x1={gradientCoords.x1} y1={gradientCoords.y1} x2={gradientCoords.x2} y2={gradientCoords.y2}>
                      {gradientData.stops.map((stop, i) => (
                        <stop key={i} offset={`${stop.offset * 100}%`} stopColor={stop.color} />
                      ))}
                    </linearGradient>
                  ) : (
                    <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
                      {gradientData.stops.map((stop, i) => (
                        <stop key={i} offset={`${stop.offset * 100}%`} stopColor={stop.color} />
                      ))}
                    </radialGradient>
                  )
                )}
                {hasElevation && (
                  <filter id={`shadow-${element.id}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy={shadowY} stdDeviation={shadowBlur / 2} floodOpacity={shadowOpacity} />
                  </filter>
                )}
                {element.content && (
                  <pattern id={`img-${element.id}`} patternUnits="objectBoundingBox" width="1" height="1">
                    <image href={element.content} x="0" y="0" width="100" height="100" preserveAspectRatio="xMidYMid slice" />
                  </pattern>
                )}
                <clipPath id={`clip-${element.id}`}>
                  <polygon points="50,0 100,100 0,100" />
                </clipPath>
              </defs>
              {element.content ? (
                <g filter={hasElevation ? `url(#shadow-${element.id})` : undefined}>
                  <image
                    href={element.content}
                    x={imgX} y={imgY} width={imgSize} height={imgSize}
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={`url(#clip-${element.id})`}
                  />
                </g>
              ) : (
                <polygon
                  points="50,0 100,100 0,100"
                  fill={triangleFill}
                  stroke={strokeWidth > 0 ? strokeColor : 'none'}
                  strokeWidth={strokeWidth}
                  vectorEffect="non-scaling-stroke"
                  filter={hasElevation ? `url(#shadow-${element.id})` : undefined}
                />
              )}
            </svg>
          );
        }

        if (shapeType === 'star') {
          // 5-zackiger Stern in 100x100 viewBox
          const starPoints = '50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35';
          const starGradientId = `gradient-star-${element.id}`;
          const starFill = strokeOnly ? 'none' : (hasGradientFill ? `url(#${starGradientId})` : shapeColor);

          return (
            <svg className="w-full h-full" viewBox="-10 -10 120 120" preserveAspectRatio="xMidYMid meet" style={{ overflow: 'visible' }}>
              <defs>
                {/* Gradient definition for star */}
                {hasGradientFill && gradientData && gradientCoords && (
                  gradientData.type === 'linear' ? (
                    <linearGradient id={starGradientId} x1={gradientCoords.x1} y1={gradientCoords.y1} x2={gradientCoords.x2} y2={gradientCoords.y2}>
                      {gradientData.stops.map((stop, i) => (
                        <stop key={i} offset={`${stop.offset * 100}%`} stopColor={stop.color} />
                      ))}
                    </linearGradient>
                  ) : (
                    <radialGradient id={starGradientId} cx="50%" cy="50%" r="50%">
                      {gradientData.stops.map((stop, i) => (
                        <stop key={i} offset={`${stop.offset * 100}%`} stopColor={stop.color} />
                      ))}
                    </radialGradient>
                  )
                )}
                {hasElevation && (
                  <filter id={`shadow-star-${element.id}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy={shadowY} stdDeviation={shadowBlur / 2} floodOpacity={shadowOpacity} />
                  </filter>
                )}
                <clipPath id={`clip-star-${element.id}`}>
                  <polygon points={starPoints} />
                </clipPath>
              </defs>
              {element.content ? (
                <g filter={hasElevation ? `url(#shadow-star-${element.id})` : undefined}>
                  <image
                    href={element.content}
                    x={imgX} y={imgY} width={imgSize} height={imgSize}
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={`url(#clip-star-${element.id})`}
                  />
                </g>
              ) : (
                <polygon
                  points={starPoints}
                  fill={starFill}
                  stroke={strokeWidth > 0 ? strokeColor : 'none'}
                  strokeWidth={strokeWidth}
                  vectorEffect="non-scaling-stroke"
                  filter={hasElevation ? `url(#shadow-star-${element.id})` : undefined}
                />
              )}
            </svg>
          );
        }

        if (shapeType === 'line') {
          return (
            <svg className="w-full h-full" viewBox="-10 -10 120 120" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
              <defs>
                {hasElevation && (
                  <filter id={`shadow-line-${element.id}`} x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy={shadowY} stdDeviation={shadowBlur / 2} floodOpacity={shadowOpacity} />
                  </filter>
                )}
              </defs>
              <line
                x1="0" y1="50"
                x2="100" y2="50"
                stroke={shapeColor}
                strokeWidth={Math.max(strokeWidth, 2)}
                vectorEffect="non-scaling-stroke"
                filter={hasElevation ? `url(#shadow-line-${element.id})` : undefined}
              />
            </svg>
          );
        }

        // Custom SVG aus Element-Bibliothek oder Phosphor Icon
        if (shapeType === 'custom') {
          // Phosphor Icon
          if (element.style.phosphorIcon) {
            const IconComponent = getPhosphorIcon(element.style.phosphorIcon);
            if (IconComponent) {
              return (
                <div
                  className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
                  style={{
                    filter: hasElevation
                      ? `drop-shadow(0 ${shadowY}px ${shadowBlur}px rgba(0,0,0,${shadowOpacity}))`
                      : undefined
                  }}
                >
                  <IconComponent
                    weight={element.style.phosphorWeight || 'regular'}
                    color={shapeColor}
                  />
                </div>
              );
            }
          }

          // Standard SVG-Content
          if (element.style.svgContent) {
            // SVG-Inhalt mit Farbe anpassen
            const coloredSvg = element.style.svgContent
              .replace(/currentColor/g, shapeColor)
              .replace(/stroke="currentColor"/g, `stroke="${shapeColor}"`)
              .replace(/fill="currentColor"/g, `fill="${shapeColor}"`);

            return (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  filter: hasElevation
                    ? `drop-shadow(0 ${shadowY}px ${shadowBlur}px rgba(0,0,0,${shadowOpacity}))`
                    : undefined
                }}
                dangerouslySetInnerHTML={{ __html: coloredSvg }}
              />
            );
          }
        }

        // Rectangle/Ellipse ohne Bild - nichts extra rendern (wird via CSS gemacht)
        return null;
      })()}

      {/* Resize Handles - dezent und nur bei Auswahl sichtbar, nicht im Preview-Modus */}
      {isSelected && !element.locked && !previewMode && (
        <>
          {/* Rotation Handle - above the element */}
          <div
            className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center opacity-60 hover:opacity-100 transition-opacity"
            style={{ top: '-24px' }}
          >
            {/* Verbindungslinie */}
            <div className="w-px h-2 bg-blue-400" />
            {/* Rotations-Griff */}
            <div
              className="w-3 h-3 bg-white/90 border border-blue-400 rounded-full cursor-grab hover:bg-blue-50 hover:border-blue-500 flex items-center justify-center shadow-sm"
              onMouseDown={handleRotateStart}
              title={`Rotation: ${Math.round(element.style.rotation || 0)}° (Shift für 15°-Schritte)`}
            >
              <svg className="w-2 h-2 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 12a8 8 0 0 1 8-8V2l4 3-4 3V6a6 6 0 0 0-6 6" />
                <path d="M20 12a8 8 0 0 1-8 8v2l-4-3 4-3v2a6 6 0 0 0 6-6" />
              </svg>
            </div>
          </div>

          {/* Corners - kleine runde Handles */}
          <div
            className="absolute -top-1 -left-1 w-2 h-2 bg-white/90 border border-blue-400 rounded-full cursor-nw-resize hover:bg-blue-100 hover:border-blue-500 shadow-sm"
            onMouseDown={(e) => handleResizeStart(e, 'nw')}
          />
          <div
            className="absolute -top-1 -right-1 w-2 h-2 bg-white/90 border border-blue-400 rounded-full cursor-ne-resize hover:bg-blue-100 hover:border-blue-500 shadow-sm"
            onMouseDown={(e) => handleResizeStart(e, 'ne')}
          />
          <div
            className="absolute -bottom-1 -left-1 w-2 h-2 bg-white/90 border border-blue-400 rounded-full cursor-sw-resize hover:bg-blue-100 hover:border-blue-500 shadow-sm"
            onMouseDown={(e) => handleResizeStart(e, 'sw')}
          />
          <div
            className="absolute -bottom-1 -right-1 w-2 h-2 bg-white/90 border border-blue-400 rounded-full cursor-se-resize hover:bg-blue-100 hover:border-blue-500 shadow-sm"
            onMouseDown={(e) => handleResizeStart(e, 'se')}
          />
          {/* Edges - noch kleinere Handles */}
          <div
            className="absolute top-1/2 -left-1 w-1.5 h-1.5 bg-white/80 border border-blue-300 rounded-full cursor-w-resize -translate-y-1/2 hover:bg-blue-100 hover:border-blue-500"
            onMouseDown={(e) => handleResizeStart(e, 'w')}
          />
          <div
            className="absolute top-1/2 -right-1 w-1.5 h-1.5 bg-white/80 border border-blue-300 rounded-full cursor-e-resize -translate-y-1/2 hover:bg-blue-100 hover:border-blue-500"
            onMouseDown={(e) => handleResizeStart(e, 'e')}
          />
          <div
            className="absolute -top-1 left-1/2 w-1.5 h-1.5 bg-white/80 border border-blue-300 rounded-full cursor-n-resize -translate-x-1/2 hover:bg-blue-100 hover:border-blue-500"
            onMouseDown={(e) => handleResizeStart(e, 'n')}
          />
          <div
            className="absolute -bottom-1 left-1/2 w-1.5 h-1.5 bg-white/80 border border-blue-300 rounded-full cursor-s-resize -translate-x-1/2 hover:bg-blue-100 hover:border-blue-500"
            onMouseDown={(e) => handleResizeStart(e, 's')}
          />
        </>
      )}
    </div>
  );
}

// Hauptkomponente
export function CanvasEditor({
  canvas,
  elements,
  selectedElementId,
  selectedElementIds = [],
  onCanvasChange,
  onElementsChange,
  onSelectElement,
  onSelectMultiple,
  onAddElement,
  onOpenElementLibrary,
  onGroupElements,
  onUngroupElements,
  previewMode: externalPreviewMode,
  onPreviewModeChange,
}: CanvasEditorProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [internalPreviewMode, setInternalPreviewMode] = useState(false);

  // Preview-Modus: extern gesteuert oder intern
  const previewMode = externalPreviewMode ?? internalPreviewMode;
  const togglePreviewMode = () => {
    const newValue = !previewMode;
    setInternalPreviewMode(newValue);
    onPreviewModeChange?.(newValue);
  };

  // Multi-Selection State (falls nicht von außen gesteuert)
  const [internalSelectedIds, setInternalSelectedIds] = useState<string[]>([]);
  const effectiveSelectedIds = selectedElementIds.length > 0 ? selectedElementIds : internalSelectedIds;

  // Marquee-Selection State (Rechteck-Ziehen zum Auswählen)
  const [marquee, setMarquee] = useState<{
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    isActive: boolean;
  } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Keyboard Handler für Gruppierung (Strg+G) und Preview-Modus (Esc)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape zum Verlassen des Preview-Modus
      if (e.key === 'Escape' && previewMode) {
        e.preventDefault();
        togglePreviewMode();
        return;
      }

      // P-Taste zum Umschalten des Preview-Modus (wenn kein Input fokussiert)
      if (e.key === 'p' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const activeEl = document.activeElement;
        if (activeEl?.tagName !== 'INPUT' && activeEl?.tagName !== 'TEXTAREA') {
          e.preventDefault();
          togglePreviewMode();
          return;
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault();
        if (effectiveSelectedIds.length > 1 && onGroupElements) {
          onGroupElements(effectiveSelectedIds);
        }
      }
      // Strg+Shift+G zum Aufheben
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G') {
        e.preventDefault();
        const selectedEl = elements.find(el => el.id === selectedElementId);
        if (selectedEl?.type === 'group' && onUngroupElements) {
          onUngroupElements(selectedEl.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [effectiveSelectedIds, selectedElementId, elements, onGroupElements, onUngroupElements, previewMode, togglePreviewMode]);

  // Marquee-Selection Handler (Rechteck-Ziehen zum Auswählen mehrerer Elemente)
  const handleMarqueeStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Nur starten wenn direkt auf Canvas-Hintergrund geklickt wird
    if (e.target !== e.currentTarget && !(e.target as HTMLElement).hasAttribute('data-canvas-bg')) {
      return;
    }
    if (previewMode) return;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    const rect = canvasEl.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMarquee({
      startX: x,
      startY: y,
      currentX: x,
      currentY: y,
      isActive: true,
    });

    e.preventDefault();
  }, [previewMode]);

  const handleMarqueeMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!marquee?.isActive) return;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    const rect = canvasEl.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, canvas.width * scale));
    const y = Math.max(0, Math.min(e.clientY - rect.top, canvas.height * scale));

    setMarquee(prev => prev ? { ...prev, currentX: x, currentY: y } : null);
  }, [marquee?.isActive, canvas.width, canvas.height, scale]);

  const handleMarqueeEnd = useCallback(() => {
    if (!marquee?.isActive) return;

    // Berechne das Auswahl-Rechteck (in Canvas-Prozent)
    const minX = Math.min(marquee.startX, marquee.currentX) / scale;
    const maxX = Math.max(marquee.startX, marquee.currentX) / scale;
    const minY = Math.min(marquee.startY, marquee.currentY) / scale;
    const maxY = Math.max(marquee.startY, marquee.currentY) / scale;

    // Nur bei ausreichender Größe (mind. 5px) Elemente auswählen
    if (maxX - minX > 5 && maxY - minY > 5) {
      // Finde alle Elemente im Rechteck
      const selectedIds = elements.filter(el => {
        // Element-Bounds in Pixel berechnen
        const elLeft = (el.position.x / 100) * canvas.width;
        const elTop = (el.position.y / 100) * canvas.height;
        const elRight = elLeft + (el.size.width / 100) * canvas.width;
        const elBottom = elTop + (el.size.height / 100) * canvas.height;

        // Prüfe ob Element im Marquee-Rechteck liegt (teilweise oder ganz)
        return elLeft < maxX && elRight > minX && elTop < maxY && elBottom > minY;
      }).map(el => el.id);

      if (selectedIds.length > 0) {
        if (onSelectMultiple) {
          onSelectMultiple(selectedIds);
        } else {
          setInternalSelectedIds(selectedIds);
        }
        // Bei genau einem Element auch onSelectElement aufrufen
        if (selectedIds.length === 1) {
          onSelectElement(selectedIds[0]);
        }
      }
    }

    setMarquee(null);
  }, [marquee, scale, elements, canvas.width, canvas.height, onSelectMultiple, onSelectElement]);

  // Handle Multi-Selection mit Shift-Click
  const handleElementSelect = useCallback((id: string, addToSelection: boolean = false) => {
    if (addToSelection) {
      const newIds = effectiveSelectedIds.includes(id)
        ? effectiveSelectedIds.filter(eid => eid !== id)
        : [...effectiveSelectedIds, id];

      if (onSelectMultiple) {
        onSelectMultiple(newIds);
      } else {
        setInternalSelectedIds(newIds);
      }
    } else {
      onSelectElement(id);
      if (onSelectMultiple) {
        onSelectMultiple([id]);
      } else {
        setInternalSelectedIds([id]);
      }
    }
  }, [effectiveSelectedIds, onSelectElement, onSelectMultiple]);

  // Berechne Scale basierend auf Container-Größe
  useEffect(() => {
    if (!containerRef.current) return;

    const updateScale = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth - 40; // Padding
      const containerHeight = container.clientHeight - 40;

      const scaleX = containerWidth / canvas.width;
      const scaleY = containerHeight / canvas.height;
      setScale(Math.min(scaleX, scaleY, 1));
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [canvas.width, canvas.height]);

  // Element(e) verschieben - berücksichtigt Multi-Selection und Gruppen
  const handleMoveElement = useCallback((id: string, dx: number, dy: number) => {
    const dxPercent = (dx / canvas.width) * 100;
    const dyPercent = (dy / canvas.height) * 100;

    // Bestimme welche Elemente verschoben werden sollen
    const movedElement = elements.find(el => el.id === id);
    let idsToMove: string[] = [id];

    // Bei Multi-Selection alle ausgewählten verschieben
    if (effectiveSelectedIds.includes(id) && effectiveSelectedIds.length > 1) {
      idsToMove = effectiveSelectedIds;
    }
    // Bei Gruppen-Element auch Kind-Elemente verschieben
    else if (movedElement?.type === 'group' && movedElement.children) {
      idsToMove = [id, ...movedElement.children];
    }
    // Element gehört zu Gruppe - auch Gruppe und Geschwister verschieben
    else if (movedElement?.groupId) {
      const group = elements.find(el => el.id === movedElement.groupId);
      if (group?.children) {
        idsToMove = [group.id, ...group.children];
      }
    }

    onElementsChange(
      elements.map((el) =>
        idsToMove.includes(el.id)
          ? {
              ...el,
              position: {
                // Elemente können auch außerhalb des Canvas positioniert werden (Clipping)
                x: el.position.x + dxPercent,
                y: el.position.y + dyPercent,
              },
            }
          : el
      )
    );
  }, [canvas.width, canvas.height, elements, onElementsChange, effectiveSelectedIds]);

  // Element resizen
  const handleResizeElement = useCallback((id: string, dx: number, dy: number, handle: string) => {
    const dxPercent = (dx / canvas.width) * 100;
    const dyPercent = (dy / canvas.height) * 100;

    onElementsChange(
      elements.map((el) => {
        if (el.id !== id) return el;

        let { x, y } = el.position;
        let { width, height } = el.size;

        // Für visuell symmetrische Elemente (Kreis, Rechteck mit lockAspectRatio):
        // Das "visuelle Seitenverhältnis" berechnen (was auf dem Bildschirm erscheint)
        // Bei Kreis soll das visuelle Verhältnis immer 1:1 sein
        const isCircle = el.type === 'shape' && el.style.shapeType === 'circle';

        // Originales visuelles Seitenverhältnis (Pixel-basiert)
        // width% * canvasWidth / height% * canvasHeight
        const originalVisualAspectRatio = (el.size.width * canvas.width) / (el.size.height * canvas.height);

        switch (handle) {
          case 'nw':
            x += dxPercent;
            y += dyPercent;
            width -= dxPercent;
            height -= dyPercent;
            break;
          case 'ne':
            y += dyPercent;
            width += dxPercent;
            height -= dyPercent;
            break;
          case 'sw':
            x += dxPercent;
            width -= dxPercent;
            height += dyPercent;
            break;
          case 'se':
            width += dxPercent;
            height += dyPercent;
            break;
          case 'n':
            y += dyPercent;
            height -= dyPercent;
            break;
          case 's':
            height += dyPercent;
            break;
          case 'w':
            x += dxPercent;
            width -= dxPercent;
            break;
          case 'e':
            width += dxPercent;
            break;
        }

        // Minimum size (Elemente können außerhalb des Canvas sein, aber nicht zu klein)
        width = Math.max(5, width);
        height = Math.max(5, height);

        // Seitenverhältnis beibehalten wenn lockAspectRatio aktiviert
        if (el.lockAspectRatio || isCircle) {
          // Bestimme welche Dimension sich mehr verändert hat (in Pixeln)
          const widthChangePx = Math.abs(width - el.size.width) * canvas.width / 100;
          const heightChangePx = Math.abs(height - el.size.height) * canvas.height / 100;

          // Für Kreise: Immer visuelles 1:1 erzwingen
          // Für andere: Das ursprüngliche visuelle Verhältnis beibehalten
          const targetVisualRatio = isCircle ? 1 : originalVisualAspectRatio;

          if (['n', 's'].includes(handle)) {
            // Vertikale Griffe: Breite anpassen basierend auf Höhe
            // Neue visuelle Höhe in Pixel: height% * canvasHeight / 100
            // Neue visuelle Breite soll sein: visuellHöhe * targetVisualRatio
            // Umrechnen zurück in Prozent: (visuellBreite / canvasWidth) * 100
            const visualHeightPx = (height / 100) * canvas.height;
            const targetVisualWidthPx = visualHeightPx * targetVisualRatio;
            const newWidth = (targetVisualWidthPx / canvas.width) * 100;
            const widthDiff = newWidth - el.size.width;
            width = newWidth;
            // Zentrieren
            x = el.position.x - widthDiff / 2;
          } else if (['e', 'w'].includes(handle)) {
            // Horizontale Griffe: Höhe anpassen basierend auf Breite
            const visualWidthPx = (width / 100) * canvas.width;
            const targetVisualHeightPx = visualWidthPx / targetVisualRatio;
            const newHeight = (targetVisualHeightPx / canvas.height) * 100;
            const heightDiff = newHeight - el.size.height;
            height = newHeight;
            // Zentrieren
            y = el.position.y - heightDiff / 2;
          } else {
            // Eck-Griffe: Größere Änderung bestimmt (in Pixeln gemessen)
            if (widthChangePx >= heightChangePx) {
              // Breite hat sich mehr verändert -> Höhe anpassen
              const visualWidthPx = (width / 100) * canvas.width;
              const targetVisualHeightPx = visualWidthPx / targetVisualRatio;
              const newHeight = (targetVisualHeightPx / canvas.height) * 100;
              // Position je nach Griff anpassen
              if (handle === 'nw' || handle === 'ne') {
                y = el.position.y + el.size.height - newHeight;
              }
              height = newHeight;
            } else {
              // Höhe hat sich mehr verändert -> Breite anpassen
              const visualHeightPx = (height / 100) * canvas.height;
              const targetVisualWidthPx = visualHeightPx * targetVisualRatio;
              const newWidth = (targetVisualWidthPx / canvas.width) * 100;
              // Position je nach Griff anpassen
              if (handle === 'nw' || handle === 'sw') {
                x = el.position.x + el.size.width - newWidth;
              }
              width = newWidth;
            }
          }

          // Minimum size nochmal prüfen nach Ratio-Anpassung
          width = Math.max(5, width);
          height = Math.max(5, height);
        }

        // Keine Bounds-Begrenzung - Elemente können außerhalb des Canvas positioniert werden

        return {
          ...el,
          position: { x, y },
          size: { width, height },
        };
      })
    );
  }, [canvas.width, canvas.height, elements, onElementsChange]);

  // Element rotieren
  const handleRotateElement = useCallback((id: string, angle: number) => {
    onElementsChange(
      elements.map((el) => {
        if (el.id !== id) return el;

        return {
          ...el,
          style: {
            ...el.style,
            rotation: angle,
          },
        };
      })
    );
  }, [elements, onElementsChange]);

  // Canvas Hintergrund
  const canvasStyle: React.CSSProperties = {
    width: canvas.width * scale,
    height: canvas.height * scale,
    position: 'relative',
    overflow: 'hidden',
  };

  if (canvas.background_type === 'color') {
    canvasStyle.backgroundColor = canvas.background_value;
  } else if (canvas.background_type === 'gradient') {
    canvasStyle.background = canvas.background_value;
  } else if (canvas.background_type === 'image') {
    canvasStyle.backgroundImage = `url(${canvas.background_value})`;
    canvasStyle.backgroundSize = 'cover';
    canvasStyle.backgroundPosition = 'center';
  }

  // Elemente nach zIndex sortieren
  const sortedElements = [...elements].sort(
    (a, b) => (a.style.zIndex ?? 0) - (b.style.zIndex ?? 0)
  );

  // Check if selected element is a group (for ungroup button)
  const selectedElement = elements.find(el => el.id === selectedElementId);
  const canUngroup = selectedElement?.type === 'group';

  // Handle group from toolbar
  const handleGroupFromToolbar = () => {
    if (effectiveSelectedIds.length > 1 && onGroupElements) {
      onGroupElements(effectiveSelectedIds);
    }
  };

  // Handle ungroup from toolbar
  const handleUngroupFromToolbar = () => {
    if (selectedElement?.type === 'group' && onUngroupElements) {
      onUngroupElements(selectedElement.id);
    }
  };

  // Get elements to align (selected or single selected element)
  const getElementsToAlign = useCallback((): CanvasElement[] => {
    if (effectiveSelectedIds.length > 0) {
      return elements.filter(el => effectiveSelectedIds.includes(el.id));
    }
    if (selectedElementId) {
      const el = elements.find(e => e.id === selectedElementId);
      return el ? [el] : [];
    }
    return [];
  }, [elements, effectiveSelectedIds, selectedElementId]);

  // Align elements to canvas or each other
  const handleAlign = useCallback((type: AlignmentType) => {
    const elementsToAlign = getElementsToAlign();
    if (elementsToAlign.length === 0) return;

    // Single element: align to canvas
    // Multiple elements: align to the bounding box of the first element
    const isSingleElement = elementsToAlign.length === 1;

    let referenceX = 0;
    let referenceY = 0;
    let referenceWidth = 100; // Canvas is 100%
    let referenceHeight = 100;

    if (!isSingleElement) {
      // Use first selected element as reference
      const refEl = elementsToAlign[0];
      referenceX = refEl.position.x;
      referenceY = refEl.position.y;
      referenceWidth = refEl.size.width;
      referenceHeight = refEl.size.height;
    }

    const newElements = elements.map(el => {
      if (!elementsToAlign.find(e => e.id === el.id)) return el;
      // Skip reference element for multi-select
      if (!isSingleElement && el.id === elementsToAlign[0].id) return el;

      let newX = el.position.x;
      let newY = el.position.y;

      switch (type) {
        case 'left':
          newX = isSingleElement ? 0 : referenceX;
          break;
        case 'center-h':
          if (isSingleElement) {
            newX = (100 - el.size.width) / 2;
          } else {
            newX = referenceX + (referenceWidth - el.size.width) / 2;
          }
          break;
        case 'right':
          if (isSingleElement) {
            newX = 100 - el.size.width;
          } else {
            newX = referenceX + referenceWidth - el.size.width;
          }
          break;
        case 'top':
          newY = isSingleElement ? 0 : referenceY;
          break;
        case 'center-v':
          if (isSingleElement) {
            newY = (100 - el.size.height) / 2;
          } else {
            newY = referenceY + (referenceHeight - el.size.height) / 2;
          }
          break;
        case 'bottom':
          if (isSingleElement) {
            newY = 100 - el.size.height;
          } else {
            newY = referenceY + referenceHeight - el.size.height;
          }
          break;
      }

      return {
        ...el,
        position: { x: newX, y: newY }
      };
    });

    onElementsChange(newElements);
  }, [elements, getElementsToAlign, onElementsChange]);

  // Distribute elements evenly
  const handleDistribute = useCallback((type: DistributeType) => {
    const elementsToDistribute = getElementsToAlign();
    if (elementsToDistribute.length < 3) return;

    // Sort elements by position
    const sorted = [...elementsToDistribute].sort((a, b) => {
      if (type === 'horizontal') {
        return a.position.x - b.position.x;
      }
      return a.position.y - b.position.y;
    });

    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    // Calculate total space and spacing
    let totalSpace: number;
    let totalElementSize: number;

    if (type === 'horizontal') {
      totalSpace = (last.position.x + last.size.width) - first.position.x;
      totalElementSize = sorted.reduce((sum, el) => sum + el.size.width, 0);
    } else {
      totalSpace = (last.position.y + last.size.height) - first.position.y;
      totalElementSize = sorted.reduce((sum, el) => sum + el.size.height, 0);
    }

    const gap = (totalSpace - totalElementSize) / (sorted.length - 1);

    // Position elements
    let currentPos = type === 'horizontal' ? first.position.x : first.position.y;

    const newElements = elements.map(el => {
      const sortedIndex = sorted.findIndex(s => s.id === el.id);
      if (sortedIndex === -1) return el;

      // First and last stay in place
      if (sortedIndex === 0 || sortedIndex === sorted.length - 1) {
        currentPos += (type === 'horizontal' ? el.size.width : el.size.height) + gap;
        return el;
      }

      const newPos = currentPos;
      currentPos += (type === 'horizontal' ? el.size.width : el.size.height) + gap;

      return {
        ...el,
        position: {
          x: type === 'horizontal' ? newPos : el.position.x,
          y: type === 'vertical' ? newPos : el.position.y,
        }
      };
    });

    onElementsChange(newElements);
  }, [elements, getElementsToAlign, onElementsChange]);

  // Check if there's a selection
  const hasSelection = selectedElementId !== null || effectiveSelectedIds.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar - im Preview-Modus ausgeblendet */}
      {!previewMode && (
        <ElementToolbar
          onAddElement={onAddElement}
          onOpenElementLibrary={onOpenElementLibrary}
          selectedCount={effectiveSelectedIds.length}
          onGroup={onGroupElements ? handleGroupFromToolbar : undefined}
          onUngroup={onUngroupElements ? handleUngroupFromToolbar : undefined}
          canUngroup={canUngroup}
          onAlign={handleAlign}
          onDistribute={handleDistribute}
          hasSelection={hasSelection}
        />
      )}

      {/* Format-Auswahl - im Preview-Modus ausgeblendet */}
      {!previewMode && (
        <FormatSelector
          currentCanvas={canvas}
          onSelect={(preset) => {
            onCanvasChange({
              ...canvas,
              width: preset.width,
              height: preset.height,
            });
          }}
        />
      )}

      {/* Canvas Container - im Preview-Modus dunklerer Hintergrund für besseren Kontrast */}
      <div
        ref={containerRef}
        className={`flex-1 flex items-center justify-center p-5 overflow-auto transition-colors ${
          previewMode ? 'bg-gray-800' : 'bg-gray-200'
        }`}
        onClick={(e) => {
          if (previewMode) return; // Im Preview-Modus keine Auswahl
          // Only deselect if clicking directly on container or canvas background
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('[data-canvas-bg]')) {
            onSelectElement(null);
          }
        }}
      >
        {/* Canvas */}
        <div
          ref={canvasRef}
          style={canvasStyle}
          className="shadow-lg relative"
          data-canvas-bg
          onMouseDown={handleMarqueeStart}
          onMouseMove={handleMarqueeMove}
          onMouseUp={handleMarqueeEnd}
          onMouseLeave={handleMarqueeEnd}
          onClick={(e) => {
            if (e.target === e.currentTarget && !marquee) {
              onSelectElement(null);
            }
          }}
        >
          {sortedElements.map((element) => (
            <CanvasElementComponent
              key={element.id}
              element={element}
              isSelected={element.id === selectedElementId || effectiveSelectedIds.includes(element.id)}
              isMultiSelected={effectiveSelectedIds.includes(element.id) && effectiveSelectedIds.length > 1}
              scale={scale}
              previewMode={previewMode}
              onSelect={(addToSelection) => handleElementSelect(element.id, addToSelection)}
              onMove={(dx, dy) => handleMoveElement(element.id, dx, dy)}
              onResize={(dx, dy, handle) => handleResizeElement(element.id, dx, dy, handle)}
              onRotate={(angle) => handleRotateElement(element.id, angle)}
            />
          ))}

          {/* Marquee-Auswahl-Rechteck */}
          {marquee?.isActive && (
            <div
              className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-none"
              style={{
                left: Math.min(marquee.startX, marquee.currentX),
                top: Math.min(marquee.startY, marquee.currentY),
                width: Math.abs(marquee.currentX - marquee.startX),
                height: Math.abs(marquee.currentY - marquee.startY),
                zIndex: 9999,
              }}
            />
          )}
        </div>
      </div>

      {/* Info & Preview Toggle */}
      <div className={`mt-2 flex items-center justify-center gap-4 transition-colors ${
        previewMode ? 'bg-gray-800 py-2 -mx-4 px-4' : ''
      }`}>
        <span className={`text-sm ${previewMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {canvas.width} x {canvas.height} px (Zoom: {Math.round(scale * 100)}%)
        </span>
        <button
          onClick={togglePreviewMode}
          className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all flex items-center gap-2 ${
            previewMode
              ? 'bg-white text-gray-800 hover:bg-gray-100 shadow-lg'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
          }`}
          title={previewMode ? t('creator:backToEdit') : t('creator:showPreview')}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {previewMode ? (
              // Bleistift-Icon für "Bearbeiten"
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            ) : (
              // Auge-Icon für "Vorschau"
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            )}
          </svg>
          {previewMode ? t('common:edit') : t('creator:preview')}
        </button>
      </div>
    </div>
  );
}
